import { ButtonContent } from '../../ui/ButtonContent';
/**
 * Calendars settings section.
 *
 * Hosts connect a calendar here so hollie can book follow-up calls on it
 * during live conversations. The section lists the current user's calendar
 * connections in the active organization only (read via RLS from the browser
 * client; tokens are never exposed), offers a connect action per available
 * provider (Google in milestone 1), and disconnects behind a confirm dialog.
 * The OAuth callback returns to this section with a `connected` or `error`
 * query param: the section quietly clears those params and, on failure, shows
 * one inline error line mapped from the error code.
 *
 * @module components/admin/settings/CalendarsSection
 */
import React, { useCallback, useEffect, useState } from 'react';
import { IconCalendar } from '@tabler/icons-react';
import { useOrg } from '../../../lib/OrgProvider';
import { useSupabase } from '../../../lib/SupabaseProvider';
import { captureException } from '../../../lib/telemetry/posthog/frontend';
import { GoogleCalendarIcon } from '../../icons/GoogleCalendarIcon';
import { ConfirmDialog } from '../../ui/ConfirmDialog';
import { ConnectedAccountRow } from './integrations/ConnectedAccountRow';
import { ProviderConnectButton } from './integrations/ProviderConnectButton';
import { useIntegrationConnect } from './integrations/useIntegrationConnect';

/** A calendar connection row as read from the browser (metadata only). */
interface CalendarConnection {
  id: string;
  provider: string;
  account_email: string;
  status: string;
}

/** Providers a host can connect. Microsoft arrives in milestone 2. */
const AVAILABLE_PROVIDERS: { id: string; label: string; icon: React.ReactNode }[] = [
  { id: 'google', label: 'Google Calendar', icon: <GoogleCalendarIcon className="w-5 h-5" /> },
];

/**
 * Short, quiet copy per OAuth callback error code. Unknown codes fall back to
 * the generic failure line.
 */
const ERROR_COPY: Record<string, string> = {
  state_invalid: 'state expired, try again',
  not_a_member: 'you are no longer a member of this organization',
  exchange_failed: 'connection failed, try again',
  profile_failed: 'connection failed, try again',
  storage_failed: 'connection failed, try again',
  declined: 'connection cancelled',
  scopes_missing: 'calendar access was not granted',
  refresh_token_missing: 'offline access was not granted',
  admin_consent_required: 'your IT admin needs to approve calendar access first',
};

/** The provider glyph for a connection row, with a calendar fallback. */
function providerIcon(provider: string): React.ReactNode {
  if (provider === 'google') return <GoogleCalendarIcon className="w-5 h-5" />;
  return <IconCalendar className="w-5 h-5" />;
}

/**
 * Calendar account rows within the shared Integrations section. Lists the
 * current user's connections in the active org and a connect action per
 * available provider.
 */
export const CalendarsSection: React.FC = () => {
  const { activeOrg } = useOrg();
  const { user, supabase } = useSupabase();

  // The connect handshake, the bfcache guard, and the one-shot callback error
  // processing are shared across every integration section; ERROR_COPY stays
  // local so the calendar wording never leaks to another kind.
  const { connecting, errorMessage, setErrorMessage, handleConnect } = useIntegrationConnect({
    kind: 'calendar',
    errorCopy: ERROR_COPY,
  });

  const [connections, setConnections] = useState<CalendarConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [disconnectTarget, setDisconnectTarget] = useState<CalendarConnection | null>(null);
  const [disconnecting, setDisconnecting] = useState(false);

  /** Load the current user's connections for the active org. Always resolves. */
  const loadConnections = useCallback(async (): Promise<void> => {
    if (!activeOrg || !user) {
      setConnections([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data, error } = await supabase
      .from('integration_connections')
      .select('id, provider, account_email, status')
      .eq('integration_type', 'calendar')
      .eq('organization_id', activeOrg.id)
      .eq('user_id', user.id);
    if (error) {
      captureException(new Error(error.message), 'CalendarsSection.loadConnections', {
        orgId: activeOrg.id,
        userId: user.id,
      });
      setConnections([]);
    } else {
      setConnections((data ?? []) as CalendarConnection[]);
    }
    setLoading(false);
  }, [supabase, activeOrg, user]);

  useEffect(() => {
    void loadConnections();
  }, [loadConnections]);

  /** A fresh Authorization header from the current session. */
  const getBearer = useCallback(async (): Promise<string> => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) throw new Error('Not authenticated');
    return `Bearer ${session.access_token}`;
  }, [supabase]);

  /**
   * Disconnect the pending target. Always closes the dialog and reconciles the
   * list so a failure never wedges the user on a stuck dialog: a 404 means the
   * row was already gone (treated as done, no error), any other failure closes
   * the dialog, refetches, and surfaces the inline error.
   */
  const handleConfirmDisconnect = useCallback(async (): Promise<void> => {
    if (!disconnectTarget) return;
    const target = disconnectTarget;
    setDisconnecting(true);
    setErrorMessage(null);
    try {
      const bearer = await getBearer();
      const res = await fetch(
        `/api/integrations/connections/${target.id}`,
        { method: 'DELETE', headers: { Authorization: bearer } },
      );
      // A 404 is a benign race: the row is already gone, so the disconnect is
      // done. Anything else that is not ok is a genuine failure.
      if (!res.ok && res.status !== 404) {
        throw new Error(`disconnect failed: ${res.status}`);
      }
    } catch (err) {
      captureException(
        err instanceof Error ? err : new Error(String(err)),
        'CalendarsSection.handleConfirmDisconnect',
        { orgId: activeOrg?.id, connectionId: target.id },
      );
      setErrorMessage('could not disconnect, try again');
    } finally {
      // Close the dialog and reconcile the list regardless of outcome: a row
      // that genuinely still exists reappears, one that is gone drops.
      setDisconnectTarget(null);
      setDisconnecting(false);
      await loadConnections();
    }
  }, [disconnectTarget, getBearer, loadConnections, activeOrg]);

  /** Providers without an existing connection get a connect row below the list. */
  const availableProviders = AVAILABLE_PROVIDERS.filter(
    (p) => !connections.some((c) => c.provider === p.id),
  );

  return (
    <section aria-label="Calendar accounts">

      {errorMessage && <p className="text-sm text-danger mb-4">{errorMessage}</p>}

      {loading ? (
        <div className="space-y-2">
          <div className="h-14 bg-surface rounded-lg animate-pulse" />
        </div>
      ) : (
        <div className="space-y-2">
          {connections.map((connection) => (
            <ConnectedAccountRow
              key={connection.id}
              icon={providerIcon(connection.provider)}
              identity={connection.account_email}
              secondary={connection.provider === 'google' ? 'Google Calendar' : connection.provider}
              status={connection.status === 'active' ? 'active' : 'attention'}
              actions={
                connection.status === 'active' ? (
                  <button
                    type="button"
                    onClick={() => setDisconnectTarget(connection)}
                    className="text-xs text-muted-foreground hover:text-danger transition-colors"
                  >
                    Disconnect
                  </button>
                ) : (
                  <button aria-busy={connecting}
                    type="button"
                    onClick={() => void handleConnect(connection.provider)}
                    disabled={connecting}
                    className="text-xs text-volt-soft light:text-volt hover:text-volt-soft transition-colors disabled:opacity-50"
                  >
                    <ButtonContent loading={connecting}>Reconnect</ButtonContent>
                  </button>
                )
              }
            />
          ))}

          {availableProviders.map((provider) => (
            <ProviderConnectButton
              key={provider.id}
              icon={provider.icon}
              label={provider.label}
              busy={connecting}
              onConnect={() => handleConnect(provider.id)}
            />
          ))}
        </div>
      )}

      <ConfirmDialog
        open={disconnectTarget !== null}
        onClose={() => setDisconnectTarget(null)}
        onConfirm={() => void handleConfirmDisconnect()}
        title="Disconnect calendar?"
        description={
          disconnectTarget
            ? `hollie will stop booking on ${disconnectTarget.account_email}.`
            : ''
        }
        confirmLabel="Disconnect"
        loading={disconnecting}
        variant="danger"
      />
    </section>
  );
};
